import React, { useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Chips } from 'primereact/chips';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import CustomToast from '../../components/CustomToast';
import JobHistory from './JobHistory';
import {
  PERM,
  EMPLOYMENT_TYPES,
  apiErrorMessage,
  apiFieldErrors,
  experienceLabel,
  fromDateOnly,
  toDateOnly,
  useCareersPermissions,
  useCareersRequest,
} from './careersShared';
import {
  CareersPage, EmptyState, JobStatusBadge, Loading, PageHeader, Panel,
} from './careersUI';
import './careers.css';

const LIST_MAX = 30;
const LIST_FIELDS = [
  { field: 'responsibilities', label: 'Responsibilities', addLabel: 'Add responsibility', placeholder: 'One point per line' },
  { field: 'qualifications', label: 'Qualifications', addLabel: 'Add qualification', placeholder: 'One point per line' },
  { field: 'benefits', label: 'Benefits', addLabel: 'Add benefit', placeholder: 'e.g. Health insurance' },
];

const EMPTY_JOB = {
  title: '',
  category_id: null,
  location: 'Kolkata, West Bengal',
  employment_type: 'Full Time',
  is_urgent: false,
  experience_min: 0,
  experience_max: 0,
  salary: '',
  vacancies: 1,
  tags: [],
  description: '',
  responsibilities: [''],
  qualifications: [''],
  benefits: [],
  application_deadline: null,
};

function formFromJob(job) {
  return {
    title: job.title,
    category_id: job.category_id,
    location: job.location,
    employment_type: job.employment_type,
    is_urgent: Boolean(job.is_urgent),
    experience_min: job.experience_min,
    experience_max: job.experience_max,
    salary: job.salary || '',
    vacancies: job.vacancies,
    tags: job.tags || [],
    description: job.description,
    responsibilities: job.responsibilities?.length ? job.responsibilities : [''],
    qualifications: job.qualifications?.length ? job.qualifications : [''],
    benefits: job.benefits || [],
    application_deadline: fromDateOnly(job.application_deadline),
  };
}

const cleanList = (items) => items.map((item) => item.trim()).filter(Boolean);

/** Request body for POST/PUT /careers/jobs. */
export function jobPayload(form) {
  return {
    title: form.title.trim(),
    category_id: form.category_id,
    location: form.location.trim(),
    employment_type: form.employment_type,
    is_urgent: form.is_urgent,
    experience_min: form.experience_min ?? 0,
    experience_max: form.experience_max ?? 0,
    salary: form.salary.trim() || null,
    vacancies: form.vacancies ?? 1,
    tags: cleanList(form.tags),
    description: form.description.trim(),
    responsibilities: cleanList(form.responsibilities),
    qualifications: cleanList(form.qualifications),
    benefits: cleanList(form.benefits),
    application_deadline: toDateOnly(form.application_deadline),
  };
}

/** Quick checks before sending; the server validates everything again. */
export function validateJobForm(form) {
  const errors = {};
  if (form.title.trim().length < 3) errors.title = 'Title must be at least 3 characters';
  if (!form.category_id) errors.category_id = 'Choose a category';
  if (!form.location.trim()) errors.location = 'Location is required';
  if (!form.description.trim()) errors.description = 'Description is required';
  if ((form.experience_min ?? 0) > (form.experience_max ?? 0)) {
    errors.experience_max = 'Maximum experience must be at least the minimum';
  }
  return errors;
}

function FieldError({ error }) {
  return error ? <small className="careers-field-error" role="alert">{error}</small> : null;
}

function ListEditor({ id, label, addLabel, hint, items, onChange, error, placeholder, disabled }) {
  const update = (index, value) => onChange(items.map((item, i) => (i === index ? value : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    onChange(next);
  };

  return (
    <div className="careers-list-editor">
      <label id={`${id}-label`} className="mb-1">{label}</label>
      {hint && <small className="careers-hint mt-0 mb-2">{hint}</small>}
      {!items.length && <small className="careers-hint mt-0 mb-2">Nothing added yet.</small>}
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="careers-list-row" key={index}>
          <span className="careers-list-index" aria-hidden="true">{index + 1}.</span>
          <InputTextarea
            value={item}
            onChange={(e) => update(index, e.target.value)}
            rows={1}
            autoResize
            placeholder={placeholder}
            aria-label={`${label} ${index + 1}`}
            disabled={disabled}
          />
          <div className="careers-list-buttons">
            <Button
              type="button"
              icon="pi pi-chevron-up"
              text
              severity="secondary"
              size="small"
              aria-label={`Move ${label} ${index + 1} up`}
              disabled={disabled || index === 0}
              onClick={() => move(index, -1)}
            />
            <Button
              type="button"
              icon="pi pi-chevron-down"
              text
              severity="secondary"
              size="small"
              aria-label={`Move ${label} ${index + 1} down`}
              disabled={disabled || index === items.length - 1}
              onClick={() => move(index, 1)}
            />
            <Button
              type="button"
              icon="pi pi-trash"
              text
              severity="danger"
              size="small"
              aria-label={`Remove ${label} ${index + 1}`}
              disabled={disabled}
              onClick={() => remove(index)}
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        label={addLabel}
        icon="pi pi-plus"
        severity="help"
        outlined
        size="small"
        className="ms-4 mt-1"
        disabled={disabled || items.length >= LIST_MAX}
        onClick={() => onChange([...items, ''])}
      />
      <FieldError error={error} />
    </div>
  );
}

const Required = () => <span className="careers-required" aria-hidden="true">*</span>;

function PortalPreview({ form, categoryName }) {
  const payload = jobPayload(form);
  const section = (title, items) => (items.length ? (
    <>
      <h6>{title}</h6>
      <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
    </>
  ) : null);
  return (
    <div className="careers-preview">
      <h3>
        {payload.title || 'Untitled job'}
        {payload.is_urgent && <Tag value="Urgent" severity="danger" className="ms-2 align-middle" />}
      </h3>
      <p className="mb-1">
        {categoryName || 'No category'} · {payload.location} · {payload.employment_type}
      </p>
      <p className="mb-1">
        {experienceLabel(payload.experience_min, payload.experience_max)} experience
        {payload.salary ? ` · ${payload.salary}` : ''}
        {' · '}{payload.vacancies} {payload.vacancies === 1 ? 'vacancy' : 'vacancies'}
      </p>
      {payload.tags.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {payload.tags.map((tag) => <Tag key={tag} value={tag} severity="secondary" />)}
        </div>
      )}
      <h6>Job description</h6>
      <p className="careers-preview-description">{payload.description || '—'}</p>
      {section('Responsibilities', payload.responsibilities)}
      {section('Qualifications', payload.qualifications)}
      {section('Benefits', payload.benefits)}
    </div>
  );
}

const JobPostingForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const request = useCareersRequest();
  const can = useCareersPermissions();
  const toastRef = useRef(null);

  const [form, setForm] = useState(EMPTY_JOB);
  const [job, setJob] = useState(null);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const canPublish = can(PERM.publishJobs);
  const isClosed = job?.status === 'closed';
  const backToList = () => navigate('/Graphe/dashboard/careers/jobs');

  useEffect(() => {
    request('/categories')
      .then((data) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, [request]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    request(`/jobs/${id}`)
      .then((data) => {
        setJob(data.job);
        setForm(formFromJob(data.job));
      })
      .catch((error) => setLoadError(apiErrorMessage(error, 'Could not load this job.')))
      .finally(() => setLoading(false));
  }, [id, isEdit, request]);

  const set = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const save = async (mode) => {
    const localErrors = validateJobForm(form);
    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      toastRef.current?.show('error', 'Check the form', 'Please fix the highlighted fields.');
      return;
    }
    setSaving(mode);
    setErrors({});
    const payload = jobPayload(form);
    try {
      if (!isEdit) {
        await request('/jobs', { method: 'post', data: { ...payload, publish: mode === 'publish' } });
      } else {
        await request(`/jobs/${id}`, { method: 'put', data: payload });
        if (mode === 'publish') await request(`/jobs/${id}/status`, { method: 'patch', data: { status: 'active' } });
      }
      navigate('/Graphe/dashboard/careers/jobs');
    } catch (error) {
      setErrors(apiFieldErrors(error));
      toastRef.current?.show('error', 'Not saved', apiErrorMessage(error));
      setSaving(null);
    }
  };

  // Active categories, plus the job's current one even if it was deactivated
  const categoryOptions = categories
    .filter((c) => c.is_active || c.category_id === form.category_id)
    .map((c) => ({ label: c.is_active ? c.name : `${c.name} (inactive)`, value: c.category_id }));
  const categoryName = categories.find((c) => c.category_id === form.category_id)?.name;


  const back = { label: 'Job Postings', onClick: backToList };

  if (loading) {
    return <CareersPage><Loading /></CareersPage>;
  }
  if (loadError) {
    return (
      <CareersPage>
        <PageHeader title="Edit job" back={back} />
        <EmptyState icon="pi-exclamation-circle" title={loadError}>
          <Button label="Back to job postings" text onClick={backToList} />
        </EmptyState>
      </CareersPage>
    );
  }

  const disabled = isClosed || Boolean(saving);

  return (
    <CareersPage>
      <CustomToast ref={toastRef} />
      <PageHeader
        title={isEdit ? (job?.title || 'Edit job') : 'New job'}
        subtitle={isEdit ? 'Update the posting. Fields marked * are required.' : 'Fill in the details, then save as a draft or publish it to the portal.'}
        back={back}
        badge={job && <JobStatusBadge status={job.status} />}
        actions={(
          <>
            {isEdit && (
              <Button label="History" icon="pi pi-history" severity="secondary" outlined onClick={() => setShowHistory(true)} />
            )}
            <Button label="Preview" icon="pi pi-eye" severity="secondary" outlined onClick={() => setShowPreview(true)} />
          </>
        )}
      />

      {isClosed && (
        <div className="careers-callout is-warning">
          <i className="pi pi-lock" aria-hidden="true" />
          <span>This job is closed, so it can’t be edited. Reopen it from the job list, or duplicate it to post a similar role.</span>
        </div>
      )}
      {job?.status === 'active' && (
        <div className="careers-callout is-info">
          <i className="pi pi-globe" aria-hidden="true" />
          <span>This job is live on the careers portal. Changes appear there as soon as you save.</span>
        </div>
      )}

      <form
        className="careers-form"
        onSubmit={(e) => { e.preventDefault(); save('save'); }}
        noValidate
      >
        <Row className="g-3">
          <Col lg={8}>
            <Panel title="Basics" icon="pi-briefcase" description="What candidates see first in the job list.">
              <Row className="g-3">
                <Col md={12}>
                  <label htmlFor="job-title">Job title<Required /></label>
                  <InputText
                    id="job-title"
                    value={form.title}
                    onChange={(e) => set('title')(e.target.value)}
                    disabled={disabled}
                    maxLength={150}
                    placeholder="e.g. Senior React Developer"
                    className={errors.title ? 'p-invalid' : ''}
                  />
                  <FieldError error={errors.title} />
                </Col>
                <Col md={6}>
                  <label htmlFor="job-category">Category<Required /></label>
                  <Dropdown
                    inputId="job-category"
                    value={form.category_id}
                    options={categoryOptions}
                    onChange={(e) => set('category_id')(e.value)}
                    placeholder="Choose a category"
                    disabled={disabled}
                    className={errors.category_id ? 'p-invalid' : ''}
                  />
                  <FieldError error={errors.category_id} />
                </Col>
                <Col md={6}>
                  <label htmlFor="job-type">Employment type<Required /></label>
                  <Dropdown
                    inputId="job-type"
                    value={form.employment_type}
                    options={EMPLOYMENT_TYPES}
                    onChange={(e) => set('employment_type')(e.value)}
                    disabled={disabled}
                  />
                  <FieldError error={errors.employment_type} />
                </Col>
                <Col md={12}>
                  <label htmlFor="job-location">Location<Required /></label>
                  <InputText
                    id="job-location"
                    value={form.location}
                    onChange={(e) => set('location')(e.target.value)}
                    disabled={disabled}
                    maxLength={150}
                    className={errors.location ? 'p-invalid' : ''}
                  />
                  <FieldError error={errors.location} />
                </Col>
              </Row>
            </Panel>

            <Panel title="Job description" icon="pi-align-left" description="Plain text. Line breaks are kept on the portal.">
              <label htmlFor="job-description">About the role<Required /></label>
              <InputTextarea
                id="job-description"
                value={form.description}
                onChange={(e) => set('description')(e.target.value)}
                rows={7}
                autoResize
                maxLength={20000}
                disabled={disabled}
                placeholder="Describe the team, the work, and what success looks like."
                className={errors.description ? 'p-invalid' : ''}
              />
              <FieldError error={errors.description} />
            </Panel>

            <Panel title="Details" icon="pi-list" description="Shown as bullet points. Use the arrows to reorder.">
              {LIST_FIELDS.map(({ field, label, addLabel, placeholder }) => (
                <ListEditor
                  key={field}
                  id={`job-${field}`}
                  label={label}
                  addLabel={addLabel}
                  items={form[field]}
                  onChange={set(field)}
                  error={errors[field]}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              ))}
            </Panel>
          </Col>

          <Col lg={4}>
            <Panel title="Requirements & pay" icon="pi-wallet">
              <Row className="g-3">
                <Col xs={6}>
                  <label htmlFor="job-exp-min">Min experience</label>
                  <InputNumber inputId="job-exp-min" value={form.experience_min} onValueChange={(e) => set('experience_min')(e.value ?? 0)} min={0} max={50} suffix=" yrs" disabled={disabled} />
                  <FieldError error={errors.experience_min} />
                </Col>
                <Col xs={6}>
                  <label htmlFor="job-exp-max">Max experience</label>
                  <InputNumber inputId="job-exp-max" value={form.experience_max} onValueChange={(e) => set('experience_max')(e.value ?? 0)} min={0} max={50} suffix=" yrs" disabled={disabled} />
                  <FieldError error={errors.experience_max} />
                </Col>
                <Col xs={12}>
                  <label htmlFor="job-vacancies">Vacancies</label>
                  <InputNumber inputId="job-vacancies" value={form.vacancies} onValueChange={(e) => set('vacancies')(e.value ?? 1)} min={1} max={999} disabled={disabled} />
                  <FieldError error={errors.vacancies} />
                </Col>
                <Col xs={12}>
                  <label htmlFor="job-salary">Salary</label>
                  <InputText
                    id="job-salary"
                    value={form.salary}
                    onChange={(e) => set('salary')(e.target.value)}
                    placeholder="e.g. ₹6L – ₹9L per year"
                    maxLength={100}
                    disabled={disabled}
                  />
                  <small className="careers-hint">Shown exactly as written. Leave empty to hide it.</small>
                  <FieldError error={errors.salary} />
                </Col>
                <Col xs={12}>
                  <label htmlFor="job-tags">Skills / tags</label>
                  <Chips
                    inputId="job-tags"
                    value={form.tags}
                    onChange={(e) => set('tags')(e.value || [])}
                    separator=","
                    max={20}
                    placeholder={form.tags.length ? '' : 'Type a skill and press Enter'}
                    disabled={disabled}
                  />
                  <FieldError error={errors.tags} />
                </Col>
              </Row>
            </Panel>

            <Panel title="Publishing" icon="pi-send">
              <label htmlFor="job-deadline">Application deadline</label>
              <Calendar
                inputId="job-deadline"
                value={form.application_deadline}
                onChange={(e) => set('application_deadline')(e.value)}
                dateFormat="dd M yy"
                minDate={isEdit ? undefined : new Date()}
                showIcon
                showButtonBar
                placeholder="No deadline"
                disabled={disabled}
              />
              <small className="careers-hint">The job leaves the portal after this date.</small>
              <FieldError error={errors.application_deadline} />

              <div className="careers-check mt-3">
                <Checkbox inputId="job-urgent" checked={form.is_urgent} onChange={(e) => set('is_urgent')(e.checked)} disabled={disabled} />
                <label htmlFor="job-urgent">
                  Mark as urgent
                  <small>Adds an “Urgent” badge on the portal.</small>
                </label>
              </div>
            </Panel>
          </Col>
        </Row>

        <div className="careers-savebar">
          <span className="careers-hint m-0">
            {isClosed ? 'Read-only' : canPublish && (!isEdit || job?.status === 'draft') ? 'Drafts are only visible here until you publish.' : 'Fields marked * are required.'}
          </span>
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" label={isClosed ? 'Back' : 'Cancel'} text severity="secondary" onClick={backToList} disabled={Boolean(saving)} />
            {!isClosed && (
              <Button
                type="submit"
                label={isEdit ? 'Save changes' : 'Save as draft'}
                icon="pi pi-save"
                severity="help"
                outlined={!isEdit || job?.status === 'draft'}
                loading={saving === 'save'}
                disabled={Boolean(saving)}
              />
            )}
            {canPublish && (!isEdit || job?.status === 'draft') && (
              <Button
                type="button"
                label={isEdit ? 'Save & publish' : 'Publish'}
                icon="pi pi-send"
                severity="help"
                loading={saving === 'publish'}
                disabled={Boolean(saving)}
                onClick={() => save('publish')}
              />
            )}
          </div>
        </div>
      </form>

      <Dialog
        header="Portal preview"
        visible={showPreview}
        style={{ width: '48rem', maxWidth: '95vw' }}
        className="careers-dialog"
        modal
        onHide={() => setShowPreview(false)}
      >
        <PortalPreview form={form} categoryName={categoryName} />
      </Dialog>

      {isEdit && (
        <JobHistory job={showHistory ? job : null} categories={categories} onHide={() => setShowHistory(false)} />
      )}
    </CareersPage>
  );
};

export default JobPostingForm;
